import type { TimeRange } from '@brighterly/lib-core-types/activation/enums'
import type { Stage } from '@brighterly/lib-core-types/discipline/enums'
import type { ChannelNotification } from '@brighterly/lib-core-types/lists/activation/enums'
import type {
  ActivationStore,
  DraftStepPersonalizationPayload,
  DraftStepSchedulePayload,
  DraftStepStartDatePayload,
} from './interfaces'
import type { ShortDaysOfWeek } from '@brighterly/lib-core-types/options/enums'

import { Expectations, StartDateType, Step } from '@brighterly/lib-core-types/activation/enums'

import { isFullArray } from '@shared/lib/collections'

// state
import { createInitialState } from './state'

const setBookingDate = (state: ActivationStore, payload: ActivationStore['bookingDate']): void => {
  state.bookingDate = payload
}
const setBookingTime = (state: ActivationStore, payload: ActivationStore['bookingTime']): void => {
  state.bookingTime = payload
}
const setAvailableSlots = (state: ActivationStore, payload: ActivationStore['availableSlots']): void => {
  state.availableSlots = payload
}

export const actions = {
  clear (this: ActivationStore): void {
    Object.assign(this, createInitialState())
  },
  isStep (this: ActivationStore, step: Step): boolean {
    return this.activationStep === step
  },
  setTutorType (this: ActivationStore, payload: ActivationStore['tutorType']): void {
    this.tutorType = payload
  },
  setProcessing (this: ActivationStore, payload: boolean): void {
    this.processing = payload
  },
  isSelectedDay (this: ActivationStore, payload: ShortDaysOfWeek): boolean {
    return this.dayOfWeek === payload
  },
  isShowTimeSlots (this: ActivationStore, payload: { day: ShortDaysOfWeek, slots: string[] }): boolean {
    const schedule = toRaw(this.schedule)
    const currentSlots = schedule[payload.day] || []
    return currentSlots.some(currentSlot => payload.slots.includes(currentSlot.from))
  },
  isCompletedDay (this: ActivationStore, payload: ShortDaysOfWeek): boolean {
    const schedule = toRaw(this.schedule)
    const currentSlots = schedule[payload] || []
    return isFullArray(currentSlots)
  },
  setDayOfWeek (this: ActivationStore, payload: ShortDaysOfWeek): void {
    this.dayOfWeek = payload
  },
  isSelectedSlot (this: ActivationStore, payload: { day: ShortDaysOfWeek, from: string }): boolean {
    const schedule = toRaw(this.schedule)
    const currentSlots = schedule[payload.day] || []
    return currentSlots.some(slot => slot.from === payload.from)
  },
  setScheduleSlot (this: ActivationStore, payload: { day: ShortDaysOfWeek, slot: TimeRange }): void {
    const schedule = toRaw(this.schedule)
    const currentSlots = schedule[payload.day] || []
    const exists = currentSlots.some(slot => slot.from === payload.slot.from)
    if (exists) {
      schedule[payload.day] = currentSlots.filter(slot => slot.from !== payload.slot.from)
    } else {
      schedule[payload.day] = [...currentSlots, payload.slot]
    }
    this.schedule = { ...schedule }
  },
  getValidSchedule (this: ActivationStore, payload: { pace: number }): boolean {
    const data = toRaw(this.schedule)
    const checkedDays = Object
      .values(data)
      .filter(day => isFullArray(day))
      .length

    this.validSchedule = checkedDays >= payload.pace

    return this.validSchedule
  },
  setToggleActivationReminder (this: ActivationStore, payload: ChannelNotification): void {
    const list = this.activationReminders
    if (list.includes(payload)) {
      const index = list.indexOf(payload)
      list.splice(index, 1)
    } else {
      list.push(payload)
    }
    this.activationReminders = [...list]
  },
  setStartDate (this: ActivationStore, payload: string): void {
    this.startDate = payload
  },
  setDraftScheduleStep (this: ActivationStore, payload: DraftStepSchedulePayload | null): void {
    this.draftStepSchedule = payload
  },
  setDraftStartDateStep (this: ActivationStore, payload: DraftStepStartDatePayload | null): void {
    this.draftStepStartDate = payload
  },
  setDraftPersonalizationStep (this: ActivationStore, payload: DraftStepPersonalizationPayload | null): void {
    this.draftStepPersonalization = payload
  },
  setDraftTutorTypeStep (this: ActivationStore, payload: boolean): void {
    this.draftStepTutorType = payload
  },
  setActivationStep (this: ActivationStore, payload: Step): void {
    this.activationStep = payload
  },
  setModalDiscardActivation (this: ActivationStore, payload: boolean): void {
    this.modalDiscardActivation = payload
  },
  setSwitcherStartDateType (this: ActivationStore, payload: StartDateType): void {
    this.switcherStartDateType = payload
  },
  setScreenRecording (this: ActivationStore, payload: boolean): void {
    this.screenRecording = payload
  },
  setExpectations (this: ActivationStore, payload: Expectations[]): void {
    this.expectations = [...payload]
  },
  setActivationCompleted (this: ActivationStore, payload: boolean): void {
    this.activationCompleted = payload
  },
  setModalBonus (this: ActivationStore, payload: boolean): void {
    this.modalBonus = payload
  },
  setBookingAvailableDays (this: ActivationStore, payload: string[] | null): void {
    this.bookingAvailableDays = payload
  },
  setStage (this: ActivationStore, payload: Stage): void {
    this.stage = payload
  },
  setBookingDate (this: ActivationStore, payload: ActivationStore['bookingDate']): void {
    return setBookingDate(this, payload)
  },
  setBookingTime (this: ActivationStore, payload: ActivationStore['bookingTime']): void {
    return setBookingTime(this, payload)
  },
  setTriggerBookingSelect (this: ActivationStore): void {
    this.triggerBookingSelect++
  },
  setAvailableTimes (this: ActivationStore, payload: any[] | null): void {
    this.availableTimes = payload
  },
  setAvailableSlots (this: ActivationStore, payload: ActivationStore['availableSlots']): void {
    return setAvailableSlots(this, payload)
  },
  setAvailableDays (this: ActivationStore, payload: string[] | null): void {
    this.availableDays = payload
  },
  setSchedule (this: ActivationStore, payload: Record<ShortDaysOfWeek, TimeRange[]>): void {
    this.schedule = { ...payload }
  },
  setActivationReminders (this: ActivationStore, payload: ChannelNotification[]): void {
    this.activationReminders = [...payload]
  },
  setModalBooked (this: ActivationStore, payload: ActivationStore['modalBooked']): void {
    this.modalBooked = payload
  },
  setModalPremiumTutorRetry (this: ActivationStore, payload: boolean): void {
    this.modalPremiumTutorRetry = payload
  },
  setPreferences (this: ActivationStore, payload: string): void {
    this.preferences = payload
  },
  setOptionsLoading (this: ActivationStore, payload: boolean): void {
    this.optionsLoading = payload
  },
  setPremiumTutorUpsellOrder (this: ActivationStore, payload: ActivationStore['premiumTutorUpsellOrder']): void {
    this.premiumTutorUpsellOrder = payload
  },
  setActivationOrderIdQuery (this: ActivationStore, payload: ActivationStore['activationOrderIdQuery']): void {
    this.activationOrderIdQuery = payload
  },
  setClearData (this: ActivationStore): void {
    setBookingDate(this, null)
    setBookingTime(this, null)
    setAvailableSlots(this, null)
  },
}
